import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
    Leaf, MapPin, Compass, MagnifyingGlass, PawPrint, Info, Target,
    Sparkle, NavigationArrow, ArrowRight, ArrowLeft, Spinner
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NEUTRAL = { bg: '#ecfccb', accent: '#16a34a', text: '#0f172a', card_bg: '#ffffff' };

// Leaflet default icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const habitatIcon = (color, emoji) => L.divIcon({
    className: '',
    html: `<div style="width:34px;height:34px;border-radius:50%;background:${color || '#16a34a'};border:3px solid #0f172a;box-shadow:0 2px 6px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;font-size:16px;">${emoji || '🌿'}</div>`,
    iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -18],
});
const youIcon = L.divIcon({
    className: '',
    html: `<div style="width:22px;height:22px;border-radius:50%;background:#2563eb;border:4px solid #fff;box-shadow:0 0 0 3px #2563eb,0 2px 8px rgba(0,0,0,.4);"></div>`,
    iconSize: [22, 22], iconAnchor: [11, 11],
});

function FitBounds({ points }) {
    const map = useMap();
    useEffect(() => {
        if (points && points.length > 0) {
            const bounds = L.latLngBounds(points).pad(0.3);
            map.fitBounds(bounds, { maxZoom: 11 });
        }
    }, [points, map]);
    return null;
}

function SightingCard({ sighting, index }) {
    const t = NEUTRAL;
    return (
        <div
            className="group block border-4 border-[#0f172a] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[5px_5px_0px_#0f172a] transition-all bg-white"
            data-testid={`adventure-sighting-${index}`}
        >
            <div className="h-32 sm:h-36 relative overflow-hidden bg-[#e2e8f0]">
                {sighting.photo_url ? (
                    <>
                        <img
                            src={sighting.photo_url}
                            alt={sighting.common_name}
                            referrerPolicy="no-referrer"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                            onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                        />
                        <div className="w-full h-full items-center justify-center absolute inset-0 hidden" style={{ backgroundColor: t.bg }}>
                            <PawPrint weight="duotone" size={42} style={{ color: t.accent, opacity: 0.4 }} />
                        </div>
                        <span
                            role="link" tabIndex={0}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(sighting.taxon_id ? `https://www.inaturalist.org/taxa/${sighting.taxon_id}` : 'https://www.inaturalist.org', '_blank', 'noopener,noreferrer'); }}
                            className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-[#0f172a]/85 text-[10px] leading-snug text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-start gap-1 backdrop-blur-sm cursor-pointer hover:underline"
                            title="View on iNaturalist"
                        >
                            <Info weight="bold" size={11} className="mt-px shrink-0" />
                            <span className="line-clamp-2">{sighting.photo_attribution || 'Photo via iNaturalist'} · iNaturalist</span>
                        </span>
                    </>
                ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: t.bg }}>
                        <PawPrint weight="duotone" size={42} style={{ color: t.accent, opacity: 0.4 }} />
                    </div>
                )}
                <div className="absolute top-2 right-2 px-2.5 py-1 rounded-lg border-2 border-[#0f172a] text-xs font-bold text-white" style={{ backgroundColor: t.accent }}>
                    {(sighting.observation_count || 0).toLocaleString()}
                </div>
            </div>
            <div className="p-3">
                <h3 className="font-fredoka font-bold text-sm leading-tight text-[#0f172a]">{sighting.common_name}</h3>
                <p className="text-xs italic mt-0.5 text-[#64748b]">{sighting.scientific_name}</p>
            </div>
        </div>
    );
}

const DIFFICULTY_COLORS = { easy: '#22c55e', medium: '#eab308', hard: '#fb7185' };

export default function AdventurePage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const goBackToPortal = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate('/dashboard');
    };
    const [status, setStatus] = useState('init'); // init | locating | ready | need-location | error
    const [coords, setCoords] = useState(null);
    const [placeName, setPlaceName] = useState('');
    const [data, setData] = useState(null);
    const [loadingData, setLoadingData] = useState(false);
    const [wildlife, setWildlife] = useState([]);
    const [wPage, setWPage] = useState(1);
    const [wTotalPages, setWTotalPages] = useState(1);
    const [wTotal, setWTotal] = useState(0);
    const [wRadius, setWRadius] = useState(25);
    const [wLoading, setWLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');

    const fetchAdventure = useCallback(async (lat, lng) => {
        setLoadingData(true);
        setStatus('ready');
        setCoords({ lat, lng });
        try {
            const { data } = await axios.get(`${API}/public/adventure`, { params: { lat, lng } });
            setData(data);
            setWildlife(data.sightings || []);
            setWPage(1);
            setWTotalPages(data.total_pages || 1);
            setWTotal(data.total_species || 0);
            setWRadius(data.sightings_radius_km || 25);
        } catch (e) {
            setData(null);
        } finally {
            setLoadingData(false);
        }
    }, []);

    const goToWildlifePage = async (p) => {
        if (!coords || p < 1 || p > wTotalPages || p === wPage || wLoading) return;
        setWLoading(true);
        try {
            const { data } = await axios.get(`${API}/public/sightings`, { params: { lat: coords.lat, lng: coords.lng, radius_km: wRadius, page: p } });
            setWildlife(data.sightings || []);
            setWPage(p);
            document.getElementById('wildlife-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (e) { /* keep current page */ } finally {
            setWLoading(false);
        }
    };

    const resolvePlaceName = useCallback(async (lat, lng) => {
        try {
            const { data } = await axios.get(`${API}/public/reverse-geocode`, { params: { lat, lng } });
            if (data.place) setPlaceName(data.place);
        } catch (e) { /* best effort */ }
    }, []);

    useEffect(() => {
        const qLat = parseFloat(searchParams.get('lat'));
        const qLng = parseFloat(searchParams.get('lng'));
        const qPlace = searchParams.get('place');
        // 1. QR / deep-link coordinates take priority
        if (!isNaN(qLat) && !isNaN(qLng)) {
            if (qPlace) setPlaceName(qPlace); else resolvePlaceName(qLat, qLng);
            fetchAdventure(qLat, qLng);
            return;
        }
        // 2. Try GPS
        if ('geolocation' in navigator) {
            setStatus('locating');
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const { latitude, longitude } = pos.coords;
                    resolvePlaceName(latitude, longitude);
                    fetchAdventure(latitude, longitude);
                },
                () => setStatus('need-location'),
                { enableHighAccuracy: true, timeout: 10000 }
            );
        } else {
            setStatus('need-location');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!query.trim()) return;
        setSearching(true);
        setSearchError('');
        try {
            const { data } = await axios.get(`${API}/public/geocode`, { params: { q: query.trim() } });
            setPlaceName(query.trim());
            fetchAdventure(data.lat, data.lng);
        } catch (err) {
            setSearchError(err.response?.data?.detail || 'Could not find that location.');
        } finally {
            setSearching(false);
        }
    };

    const mapPoints = data ? [
        [coords.lat, coords.lng],
        ...data.habitats.map((h) => [h.nearest_location.lat, h.nearest_location.lng]),
    ] : [];

    return (
        <div className="min-h-screen bg-[#fefce8] dot-grid">
            {/* Nav */}
            <nav className="bg-white border-b-4 border-[#0f172a] sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="adventure-logo">
                        <div className="w-9 h-9 rounded-full bg-[#22c55e] border-2 border-[#0f172a] flex items-center justify-center">
                            <Leaf weight="fill" className="text-white" size={20} />
                        </div>
                        <span className="font-fredoka text-lg font-bold text-[#0f172a]">Bush Rangers</span>
                    </Link>
                    {isAuthenticated ? (
                        <button onClick={goBackToPortal} className="inline-flex items-center gap-1.5 bg-[#0f172a] text-white font-fredoka font-bold text-sm px-4 py-2 rounded-full border-3 border-[#0f172a] shadow-[3px_3px_0px_#22c55e] hover:-translate-y-0.5 transition-transform" data-testid="adventure-back-nav">
                            <ArrowLeft weight="bold" size={16} /> Back to Portal
                        </button>
                    ) : (
                        <Link to="/register" className="bg-[#22c55e] text-white font-fredoka font-bold text-sm px-4 py-2 rounded-full border-3 border-[#0f172a] shadow-[3px_3px_0px_#0f172a] hover:-translate-y-0.5 transition-transform" data-testid="adventure-join-nav">
                            Join Free
                        </Link>
                    )}
                </div>
            </nav>

            <main className="max-w-6xl mx-auto px-4 py-8" data-testid="adventure-page">
                {/* Hero */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#22c55e]/15 border-2 border-[#22c55e] rounded-full mb-4">
                        <Compass weight="fill" className="text-[#16a34a]" size={18} />
                        <span className="font-fredoka font-bold text-sm text-[#16a34a]">Bush Rangers Adventure</span>
                    </div>
                    <h1 className="font-fredoka text-4xl sm:text-5xl font-bold text-[#0f172a] mb-3">
                        {status === 'ready' && placeName
                            ? <>Discover nature near <span className="text-[#16a34a]">{placeName}</span></>
                            : <>Discover the wild around <span className="text-[#16a34a]">you</span></>}
                    </h1>
                    <p className="text-base text-[#475569] max-w-xl mx-auto">
                        See the habitats, real wildlife sightings and missions happening in your area — then join the adventure!
                    </p>
                </div>

                {/* Location states */}
                {status === 'locating' && (
                    <div className="brutalist-card p-8 text-center max-w-md mx-auto" data-testid="adventure-locating">
                        <Spinner weight="bold" size={40} className="text-[#22c55e] animate-spin mx-auto mb-3" />
                        <p className="font-fredoka text-lg font-bold">Finding your location…</p>
                        <p className="text-sm text-[#475569] mt-1">Please allow location access when prompted.</p>
                    </div>
                )}

                {status === 'need-location' && (
                    <div className="brutalist-card p-6 sm:p-8 max-w-md mx-auto" data-testid="adventure-search">
                        <div className="text-center mb-4">
                            <MapPin weight="fill" size={40} className="text-[#fb7185] mx-auto mb-2" />
                            <p className="font-fredoka text-lg font-bold">Where are you exploring?</p>
                            <p className="text-sm text-[#475569]">Enter a suburb or postcode to begin.</p>
                        </div>
                        <form onSubmit={handleSearch} className="flex gap-2">
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="e.g. Bondi or 2026"
                                className="flex-1 px-4 py-3 rounded-xl border-4 border-[#0f172a] font-semibold focus:outline-none focus:ring-2 focus:ring-[#22c55e]"
                                data-testid="adventure-search-input"
                            />
                            <button type="submit" disabled={searching} className="bg-[#22c55e] text-white px-4 rounded-xl border-4 border-[#0f172a] shadow-[3px_3px_0px_#0f172a] disabled:opacity-50" data-testid="adventure-search-btn">
                                {searching ? <Spinner weight="bold" size={22} className="animate-spin" /> : <MagnifyingGlass weight="bold" size={22} />}
                            </button>
                        </form>
                        {searchError && <p className="text-[#fb7185] text-sm font-bold mt-2" data-testid="adventure-search-error">{searchError}</p>}
                    </div>
                )}

                {/* Content */}
                {status === 'ready' && (
                    <>
                        {loadingData && (
                            <div className="text-center py-12" data-testid="adventure-loading">
                                <Spinner weight="bold" size={40} className="text-[#22c55e] animate-spin mx-auto mb-3" />
                                <p className="font-fredoka font-bold">Mapping your adventure…</p>
                            </div>
                        )}

                        {!loadingData && data && (
                            <div className="space-y-10">
                                {/* Change location */}
                                <div className="flex justify-center">
                                    <button
                                        onClick={() => { setStatus('need-location'); setData(null); }}
                                        className="inline-flex items-center gap-1.5 text-sm font-bold text-[#16a34a] hover:underline"
                                        data-testid="adventure-change-location"
                                    >
                                        <NavigationArrow weight="bold" size={16} /> Change location
                                    </button>
                                </div>

                                {/* Map */}
                                <section>
                                    <h2 className="font-fredoka text-2xl font-bold text-[#0f172a] mb-3 flex items-center gap-2">
                                        <MapPin weight="fill" className="text-[#16a34a]" /> Habitats in your region
                                    </h2>
                                    <div className="rounded-2xl overflow-hidden border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a]" style={{ height: 400 }} data-testid="adventure-map">
                                        <MapContainer center={[coords.lat, coords.lng]} zoom={9} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
                                            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                            <Marker position={[coords.lat, coords.lng]} icon={youIcon}><Popup>You are here</Popup></Marker>
                                            {data.habitats.map((h) => (
                                                <Marker key={h.id} position={[h.nearest_location.lat, h.nearest_location.lng]} icon={habitatIcon(h.theme?.accent, h.emoji)}>
                                                    <Popup>
                                                        <div style={{ minWidth: 170 }}>
                                                            <p style={{ fontWeight: 'bold', margin: '0 0 2px' }}>{h.emoji} {h.name}</p>
                                                            <p style={{ fontSize: 12, color: '#475569', margin: 0 }}>{h.nearest_location.name}</p>
                                                            <p style={{ fontSize: 12, color: '#16a34a', fontWeight: 'bold', margin: '4px 0 0' }}>{h.distance_km} km away</p>
                                                        </div>
                                                    </Popup>
                                                </Marker>
                                            ))}
                                            <FitBounds points={mapPoints} />
                                        </MapContainer>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-3" data-testid="adventure-habitat-chips">
                                        {data.habitats.map((h) => (
                                            <span key={h.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-[#0f172a] bg-white text-sm font-bold" style={{ color: h.theme?.text }}>
                                                <span>{h.emoji}</span>{h.name}
                                                <span className="text-[#16a34a]">· {h.distance_km}km</span>
                                            </span>
                                        ))}
                                    </div>
                                </section>

                                {/* Wildlife */}
                                <section id="wildlife-section" className="scroll-mt-20">
                                    <h2 className="font-fredoka text-2xl font-bold text-[#0f172a] mb-1 flex items-center gap-2">
                                        <PawPrint weight="fill" className="text-[#16a34a]" /> Wildlife spotted near you
                                    </h2>
                                    <p className="text-sm text-[#475569] mb-4">
                                        {wTotal.toLocaleString()} species recorded within {wRadius}km · powered by iNaturalist
                                    </p>
                                    {wildlife.length > 0 ? (
                                        <>
                                            <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 transition-opacity ${wLoading ? 'opacity-40' : 'opacity-100'}`}>
                                                {wildlife.map((s, i) => <SightingCard key={s.taxon_id || i} sighting={s} index={i} />)}
                                            </div>
                                            {wTotalPages > 1 && (
                                                <div className="flex items-center justify-center gap-3 mt-6" data-testid="wildlife-pager">
                                                    <button
                                                        onClick={() => goToWildlifePage(wPage - 1)}
                                                        disabled={wPage <= 1 || wLoading}
                                                        className="px-4 py-2 rounded-xl border-4 border-[#0f172a] bg-white font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-40 disabled:shadow-none hover:-translate-y-0.5 transition-transform"
                                                        data-testid="wildlife-prev"
                                                    >
                                                        ‹ Prev
                                                    </button>
                                                    <span className="font-fredoka font-bold text-[#0f172a] text-sm" data-testid="wildlife-page-indicator">
                                                        Page {wPage} of {wTotalPages}
                                                    </span>
                                                    <button
                                                        onClick={() => goToWildlifePage(wPage + 1)}
                                                        disabled={wPage >= wTotalPages || wLoading}
                                                        className="px-4 py-2 rounded-xl border-4 border-[#0f172a] bg-[#22c55e] text-white font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-40 disabled:shadow-none hover:-translate-y-0.5 transition-transform"
                                                        data-testid="wildlife-next"
                                                    >
                                                        Next ›
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="brutalist-card p-6 text-center text-[#475569]">No recent sightings found in this area yet.</div>
                                    )}
                                </section>

                                {/* Missions */}
                                <section>
                                    <h2 className="font-fredoka text-2xl font-bold text-[#0f172a] mb-4 flex items-center gap-2">
                                        <Target weight="fill" className="text-[#16a34a]" /> Missions you can do here
                                    </h2>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="adventure-missions">
                                        {data.missions.map((m, i) => (
                                            <div key={m.id} className="brutalist-card p-4 flex flex-col" data-testid={`adventure-mission-${i}`}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-md border-2 border-[#0f172a] text-white" style={{ backgroundColor: DIFFICULTY_COLORS[m.difficulty] || '#22c55e' }}>{m.difficulty}</span>
                                                    <span className="text-xs font-bold text-[#eab308] flex items-center gap-1"><Sparkle weight="fill" size={12} />{m.xp_reward} tokens</span>
                                                </div>
                                                <h3 className="font-fredoka font-bold text-lg text-[#0f172a] leading-tight">{m.title}</h3>
                                                <p className="text-sm text-[#475569] mt-1 flex-1">{m.description}</p>
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                {/* CTA */}
                                {isAuthenticated ? (
                                    <section className="bg-[#0f172a] border-4 border-[#0f172a] rounded-3xl shadow-[6px_6px_0px_#22c55e] p-8 text-center" data-testid="adventure-cta">
                                        <h2 className="font-fredoka text-3xl font-bold text-white mb-2">Ready to take action?</h2>
                                        <p className="text-white/80 mb-5 max-w-lg mx-auto">Head back to your portal to start missions, earn Bush Tokens and customise your Ranger.</p>
                                        <button onClick={goBackToPortal} className="inline-flex items-center gap-2 bg-[#22c55e] text-white font-fredoka font-bold text-lg px-6 py-3 rounded-2xl border-4 border-white shadow-[4px_4px_0px_#22c55e] hover:-translate-y-0.5 transition-transform" data-testid="adventure-cta-back">
                                            <ArrowLeft weight="bold" size={20} /> Back to Portal
                                        </button>
                                    </section>
                                ) : (
                                    <section className="bg-[#22c55e] border-4 border-[#0f172a] rounded-3xl shadow-[6px_6px_0px_#0f172a] p-8 text-center" data-testid="adventure-cta">
                                        <h2 className="font-fredoka text-3xl font-bold text-white mb-2">Ready to become a Bush Ranger?</h2>
                                        <p className="text-white/90 mb-5 max-w-lg mx-auto">Earn Bush Tokens, collect badges, customise your Ranger and protect Aussie wildlife — all for free.</p>
                                        <Link to="/register" className="inline-flex items-center gap-2 bg-white text-[#16a34a] font-fredoka font-bold text-lg px-6 py-3 rounded-2xl border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] hover:-translate-y-0.5 transition-transform" data-testid="adventure-cta-signup">
                                            Start Your Adventure <ArrowRight weight="bold" size={20} />
                                        </Link>
                                    </section>
                                )}
                            </div>
                        )}

                        {!loadingData && !data && (
                            <div className="brutalist-card p-8 text-center max-w-md mx-auto">
                                <p className="font-fredoka font-bold text-lg mb-2">Hmm, we couldn't load your adventure.</p>
                                <button onClick={() => { setStatus('need-location'); }} className="text-[#16a34a] font-bold hover:underline">Try another location</button>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
